import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Container,
  Button,
  Header,
  Grid,
  Icon,
  Message,
  Segment,
  Modal,
} from "semantic-ui-react";
import { NavLink, useSearchParams } from "react-router-dom";
import { fetchScheduledBookings } from "../../reducers/booking";
import { cancel, rescheduleOwn } from "../../lib/bookingServices";
import { sendEmail } from "../../lib/emailService";
import BookingCancelled from "../emails/BookingCancelled";
import ExamBlock from "./ExamBlock";
import ConfirmationModal from "../ConfirmationModal";
import SelectDates from "./SelectDates";
import { withTranslation } from "react-i18next";
import { format } from "date-fns";
import { fr as frLocale, enUS } from "date-fns/locale";
import { TZDate } from "@date-fns/tz";
import parseTimezone from "../../lib/helperServices";
import i18n from "../../i18n";

// Maps stable error codes returned by the cancel/reschedule-own endpoints to
// translation keys — the server's `error` text is English-only (for logs),
// so codes are what drive user-facing, translated messaging.
const CANCEL_ERROR_KEYS = {
  CHECKED_IN: "cancelErrorCheckedIn",
  FINAL_STATE: "cancelErrorFinalState",
  RESCHEDULING_NOT_ALLOWED: "cancelErrorNotAllowed",
};

const RESCHEDULE_ERROR_KEYS = {
  CHECKED_IN: "rescheduleErrorCheckedIn",
  FINAL_STATE: "rescheduleErrorFinalState",
  RESCHEDULING_NOT_ALLOWED: "rescheduleErrorNotAllowed",
  WITHIN_CUTOFF: "rescheduleErrorWithin24Hours",
  SLOT_UNAVAILABLE: "rescheduleErrorUnavailable",
};

function ScheduledExams({ bookings, kc, t, fetchScheduledBookings }) {
  const [searchParams] = useSearchParams();
  const emailVerified = searchParams.get('emailVerified') === 'true';
  const [openConfirmCancelModal, setOpenConfirmCancelModal] = useState(false);
  const [openRescheduleModal, setOpenRescheduleModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);
  const [cancellationResultStatus, setCancellationResultStatus] = useState("noRefund");
  const [rescheduleError, setRescheduleError] = useState(null);
  const [rescheduleSuccess, setRescheduleSuccess] = useState(false);
  const [requiresApproval, setRequiresApproval] = useState(false);

  useEffect(() => {
    if (kc.tokenParsed?.sub) {
      fetchScheduledBookings(kc.tokenParsed.sub);
    }
  }, [kc.tokenParsed?.sub, fetchScheduledBookings]);

  // Calculate if booking is more than 24 hours away
  const getHoursUntilExam = (bookingDate) => {
    const now = new Date();
    const examDate = new Date(bookingDate);
    const diffMs = examDate - now;
    return diffMs / (1000 * 60 * 60); // Convert to hours
  };

  const handleReschedule = (bookingId) => {
    setRescheduleError(null);
    setRescheduleSuccess(false);
    // Find the booking details
    const booking = sortedBookings.find(b => b.id === bookingId);
    
    // Check if booking is within 24 hours
    const hoursUntilExam = getHoursUntilExam(booking.booking_date);
    if (hoursUntilExam <= 24) {
      setRescheduleError(t("ScheduledExams.rescheduleErrorWithin24Hours"));
      // Hide error message after 7 seconds
      setTimeout(() => setRescheduleError(null), 7000);
      return;
    }
    
    setSelectedBooking(booking);
    setOpenRescheduleModal(true);
  };

  const handleRescheduleSubmit = async (rescheduleData) => {
    try {
      const response = await rescheduleOwn({
        id: selectedBooking.id,
        examDate: rescheduleData.examDate,
        user_id: kc.tokenParsed.sub,
      });
      
      setOpenRescheduleModal(false);
      setSelectedBooking(null);
      setRescheduleSuccess(true);
      setRequiresApproval(response?.response?.data?.requiresApproval || false);
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setRescheduleSuccess(false);
        setRequiresApproval(false);
      }, 5000);
      
      // Refresh the bookings list
      if (kc.tokenParsed?.sub) {
        fetchScheduledBookings(kc.tokenParsed.sub);
      }
    } catch (error) {
      console.error('Error rescheduling booking:', error);
      setOpenRescheduleModal(false);

      const key = RESCHEDULE_ERROR_KEYS[error.response?.data?.code];
      const errorMessage = key
        ? t(`ScheduledExams.${key}`)
        : t("ScheduledExams.rescheduleError", "Failed to reschedule booking. Please try again.");

      setRescheduleError(errorMessage);
      // Hide error message after 7 seconds
      setTimeout(() => setRescheduleError(null), 7000);
    }
  };

  const handleCancel = (bookingId) => {
    setCancelError(null);
    setCancelSuccess(false);
    // Find the booking details
    const booking = sortedBookings.find(b => b.id === bookingId);
    setSelectedBooking(booking);
    setOpenConfirmCancelModal(true);
  };

  const submitCancel = async () => {
    try {
      const response = await cancel(selectedBooking.id, refunded);
      // The server recomputes refund eligibility from the booking's own
      // date/time — trust its answer over our pre-request client estimate.
      const wasActuallyRefunded = response?.refunded || false;
      // wasCharged distinguishes "no refund because you cancelled within the
      // cutoff on a charged booking" from "no refund because nothing was
      // ever charged in the first place" — those need different copy.
      const wasCharged = response?.wasCharged ?? true;
      const cancellationStatus = wasActuallyRefunded
        ? "refunded"
        : wasCharged
        ? "noRefund"
        : "notCharged";
      setOpenConfirmCancelModal(false);

      // Send cancellation email
      try {
        const locale = kc.tokenParsed.locale || "en";
        const emailT = i18n.getFixedT(locale);

        // Get timezone info
        let userTimezone = parseTimezone(
          kc.tokenParsed.timezone,
          kc.tokenParsed.province
        );
        let timezone_array = kc.tokenParsed.timezone?.split("") || [];
        const timezoneAbbr = timezone_array.length > 0
          ? timezone_array[0] + "T"
          : "ET";

        // Format booking date
        const tzDate = new TZDate(selectedBooking.booking_date, userTimezone);
        const formattedDate = format(tzDate, "MMMM do yyyy - h:mm a");

        // Calculate refund amount
        let refundAmount = null;
        if (wasActuallyRefunded && selectedBooking.request) {
          refundAmount = (selectedBooking.request.amount + selectedBooking.request.taxes).toFixed(2);
        }

        const emailProps = {
          t: emailT,
          name: `${kc.tokenParsed.given_name} ${kc.tokenParsed.family_name}`,
          examTitle: selectedBooking.exam.title,
          bookingDate: formattedDate,
          timezone: timezoneAbbr,
          duration: selectedBooking.exam.duration,
          status: cancellationStatus,
          refundAmount,
        };

        const subject = wasActuallyRefunded
          ? emailT("Emails.BookingCancelled.subjectWithRefund")
          : emailT("Emails.BookingCancelled.subjectNoRefund");

        await sendEmail({
          to: kc.tokenParsed.email,
          subject: subject,
          component: <BookingCancelled {...emailProps} />,
        });
      } catch (emailError) {
        console.error("[submitCancel] Failed to send cancellation email:", emailError);
      }
      
      setSelectedBooking(null);
      setCancelSuccess(true);
      setCancellationResultStatus(cancellationStatus);
      // Hide success message after 5 seconds
      setTimeout(() => {
        setCancelSuccess(false);
        setCancellationResultStatus("noRefund");
      }, 5000);
      // Refresh the bookings list
      if (kc.tokenParsed?.sub) {
        fetchScheduledBookings(kc.tokenParsed.sub);
      }
    } catch (error) {
      console.error('Error canceling booking:', error);
      setOpenConfirmCancelModal(false);

      const key = CANCEL_ERROR_KEYS[error.response?.data?.code];
      const errorMessage = key
        ? t(`ScheduledExams.${key}`)
        : t("ScheduledExams.cancelError", "Failed to cancel booking. Please try again.");

      setCancelError(errorMessage);
      // Hide error message after 7 seconds
      setTimeout(() => setCancelError(null), 7000);
    }
  };

  const refunded = selectedBooking ? getHoursUntilExam(selectedBooking.booking_date) > 24 : false;

  if (!kc.tokenParsed) {
    return <div>Loading...</div>;
  }

  let timezone = parseTimezone(
    kc.tokenParsed.timezone,
    kc.tokenParsed.province
  );
  let timezone_array = kc.tokenParsed.timezone?.split("") || [];
  const lang = localStorage.getItem("lang");
  const dateLocale = lang === "fr" ? frLocale : enUS;
  const dateFormat =
    lang === "en"
      ? "MMMM do yyyy - h:mm a"
      : "d MMMM yyyy - HH:mm";

  // Sort bookings by date
  const userBookings = (bookings || []).filter(
    (data) => data.user_id === kc.tokenParsed?.sub
  );
  const sortedBookings = [...userBookings].sort(
    (a, b) => new Date(a.booking_date) - new Date(b.booking_date)
  );

  return (
    <div>
      <ConfirmationModal
        open={openConfirmCancelModal}
        close={() => setOpenConfirmCancelModal(false)}
        submit={submitCancel}
        headerText={t("ScheduledExams.cancelBooking", "Cancel Exam")}
        confirmationMsg={
          refunded
            ? t(
                "ScheduledExams.confirmCancelMore24",
                "Are you sure you want to cancel this exam? Because you are canceling more than 24 hours before the start time, there is no fee and you will receive a full refund (if payment was made). Click Confirm to proceed with the cancellation."
              )
            : t(
                "ScheduledExams.confirmCancelLess24",
                "Are you sure you want to cancel this exam? This cancellation is occurring within 24 hours of your scheduled start time. No refund will be issued. Click Confirm to continue."
              )
        }
        confirmButtonText={t("ScheduledExams.confirmButton", "Confirm")}
      />
      
      <Modal
        open={openRescheduleModal}
        onClose={() => {
          setOpenRescheduleModal(false);
          setSelectedBooking(null);
        }}
        size="small"
      >
        <Modal.Header>{t("ScheduledExams.rescheduleExam", "Reschedule Exam")}</Modal.Header>
        <Modal.Content>
          {selectedBooking && (
            <SelectDates
              formData={{
                exam: selectedBooking.exam,
              }}
              onForward={handleRescheduleSubmit}
              onBackward={() => {
                setOpenRescheduleModal(false);
                setSelectedBooking(null);
              }}             
             submitButtonText={t("general.submit", "Submit")}       
                    step={2}
            />
          )}
        </Modal.Content>
      </Modal>
      
      <Container style={{ marginTop: "2em", marginBottom: "3em" }}>
        {emailVerified && (
          <Message positive style={{ marginBottom: "2em" }}>
            <Message.Header style={{ textAlign: "center", fontSize: "1.3em" }}>
              <Icon name="check circle" />
              {t("EmailVerified.success")}
            </Message.Header>
          </Message>
        )}

        {cancelSuccess && (
          <Message positive style={{ marginBottom: "2em" }}>
            <Message.Header style={{ textAlign: "center", fontSize: "1.3em" }}>
              <Icon name="check circle" />
              {t("ScheduledExams.cancelSuccess", "Booking cancelled successfully")}
            </Message.Header>
            {cancellationResultStatus === "refunded" && (
              <p style={{ textAlign: "center", marginTop: "0.5em" }}>
                {t(
                  "ScheduledExams.refundIssued",
                  "A full refund will be processed within 5-10 business days and will appear on the original payment method used for this booking."
                )}
              </p>
            )}
            {cancellationResultStatus === "noRefund" && (
              <p style={{ textAlign: "center", marginTop: "0.5em" }}>
                {t("ScheduledExams.noRefund", "No refund will be issued due to cancellation within 24 hours.")}
              </p>
            )}
            {cancellationResultStatus === "notCharged" && (
              <p style={{ textAlign: "center", marginTop: "0.5em" }}>
                {t("ScheduledExams.noChargeMade", "No payment was ever collected for this booking, so there's nothing to refund. Any card hold has been released.")}
              </p>
            )}
          </Message>
        )}

        {cancelError && (
          <Message negative style={{ marginBottom: "2em" }}>
            <Message.Header style={{ textAlign: "center", fontSize: "1.3em" }}>
              <Icon name="exclamation triangle" />
              {t("ScheduledExams.cancelErrorTitle", "Error")}
            </Message.Header>
            <p style={{ textAlign: "center" }}>{cancelError}</p>
          </Message>
        )}

        {rescheduleSuccess && (
          <Message positive style={{ marginBottom: "2em" }}>
            <Message.Header style={{ textAlign: "center", fontSize: "1.3em" }}>
              <Icon name="check circle" />
              {t("ScheduledExams.rescheduleSuccess", "Exam rescheduled successfully")}
            </Message.Header>
            {requiresApproval && (
              <p style={{ textAlign: "center", marginTop: "0.5em" }}>
                {t("ScheduledExams.requiresReapproval", "Your booking status has been changed to pending and will require re-approval.")}
              </p>
            )}
          </Message>
        )}

        {rescheduleError && (
          <Message negative style={{ marginBottom: "2em" }}>
            <Message.Header style={{ textAlign: "center", fontSize: "1.3em" }}>
              <Icon name="exclamation triangle" />
              {t("ScheduledExams.rescheduleErrorTitle", "Error")}
            </Message.Header>
            <p style={{ textAlign: "center" }}>{rescheduleError}</p>
          </Message>
        )}

        {/* Main Content with Sidebar */}
        <Grid stackable>
          <Grid.Row>
            {/* Left Column - Main Content */}
            <Grid.Column width={11}>
              {/* Action Buttons */}
              <div style={{ textAlign: "center", marginBottom: "2em" }}>
                <Button
                  as={NavLink}
                  to="/exams/book/"
                  positive
                  size="huge"
                  style={{
                    marginRight: "1em",
                    marginBottom: "1em",
                    minWidth: "280px",
                    fontSize: "1.3em",
                    padding: "1.2em 2em",
                    fontWeight: "600",
                  }}
                >
                  <Icon name="calendar plus" />
                  {t("ScheduledExams.bookExam")}
                </Button>
                <Button
                  as={NavLink}
                  to="/download"
                  positive
                  size="huge"
                  style={{
                    marginBottom: "1em",
                    minWidth: "280px",
                    fontSize: "1.3em",
                    padding: "1.2em 2em",
                    fontWeight: "600",
                  }}
                >
                  <Icon name="download" />
                  {t("ScheduledExams.downloadApplication")}
                </Button>
              </div>

              {/* Scheduled Exams */}
              {sortedBookings.length > 0 && (
                <div>
                  <Header as="h2" style={{ marginBottom: "1.5em" }}>
                    {t("ScheduledExams.header", "Scheduled Exams")}
                  </Header>
                  <Grid>
                    <Grid.Row>
                      {sortedBookings.map((data, i) => (
                        <Grid.Column
                          key={`${i}_exam`}
                          mobile={16}
                          tablet={8}
                          computer={8}
                          style={{ marginBottom: "1em" }}
                        >
                          <ExamBlock
                            timezone={`${timezone_array[0]}${timezone_array[2]}`}
                            past={false}
                            date={format(
                              new TZDate(data.booking_date, timezone),
                              dateFormat,
                              { locale: dateLocale }
                            )}
                            status={data.approved}
                            title={data.exam.title !== undefined ? data.exam.title : ""}
                            bookingId={data.id}
                            allowRescheduling={!data.client_id || !!data.client?.allow_rescheduling}
                            onReschedule={handleReschedule}
                            onCancel={handleCancel}
                          />
                        </Grid.Column>
                      ))}
                    </Grid.Row>
                  </Grid>
                </div>
              )}

              {/* No exams message */}
              {userBookings.length === 0 && (
                <Segment placeholder textAlign="center" style={{ padding: "3em" }}>
                  <Header icon>
                    <Icon name="calendar outline" />
                    {t("ScheduledExams.noExams", "No Scheduled Exams")}
                  </Header>
                  <Button as={NavLink} to="/exams/book/" positive>
                    {t("ScheduledExams.bookExam")}
                  </Button>
                </Segment>
              )}
            </Grid.Column>

            {/* Right Column - Sidebar (Always Visible) */}
            <Grid.Column width={5}>
              <Segment style={{ padding: "1.5em", position: "sticky", top: "2em" }}>
                <Header as="h3" style={{ marginBottom: "1em" }}>
                  <Icon name="clipboard list" />
                  {t("ScheduledExams.quickGuide", "Quick Guide")}
                </Header>
                <div style={{ lineHeight: "2em" }}>
                  <div style={{ marginBottom: "0.8em" }}>
                    <Icon name="check circle outline" color="green" />
                    <span style={{ marginLeft: "0.5em" }}>
                      {t("ScheduledExams.step1", "Download app 30 min before exam")}
                    </span>
                  </div>
                  <div style={{ marginBottom: "0.8em" }}>
                    <Icon name="check circle outline" color="green" />
                    <span style={{ marginLeft: "0.5em" }}>
                      {t("ScheduledExams.step2", "Log in and select your exam")}
                    </span>
                  </div>
                  <div style={{ marginBottom: "0.8em" }}>
                    <Icon name="check circle outline" color="green" />
                    <span style={{ marginLeft: "0.5em" }}>
                      {t("ScheduledExams.step3", "Follow proctor instructions")}
                    </span>
                  </div>
                  <div style={{ marginBottom: "0.8em" }}>
                    <Icon name="check circle outline" color="green" />
                    <span style={{ marginLeft: "0.5em" }}>
                      {t("ScheduledExams.step4", "Complete exam when ready")}
                    </span>
                  </div>
                </div>
                <Message warning size="small" style={{ marginTop: "1.5em" }}>
                  <Message.Header>
                    <Icon name="warning sign" />
                    {t("ScheduledExams.important", "Important")}
                  </Message.Header>
                  <p style={{ marginTop: "0.5em", fontSize: "0.9em" }}>
                    {t("ScheduledExams.latestVersionNote", "Ensure you have the latest version of the eProctor application to avoid technical difficulties.")}
                  </p>
                </Message>
              </Segment>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Container>
    </div>
  );
}

export default withTranslation()(
  connect(
    (state) => ({
      bookings: state.bookings.scheduledBookings,
      kc: state.keycloak,
    }),
    { fetchScheduledBookings }
  )(ScheduledExams)
);
